/**
 * PageHero — hero éditorial standardisé du profil Particulier.
 *
 * Composition complète d'un hero magazine "particulier-style" :
 *  - Fond ParticulierBackground (dot-grid + spot-glow + 2 halos colorés)
 *  - Breadcrumb (optionnel)
 *  - Eyebrow chip (Linear-style avec live-dot + icône)
 *  - Headline avec accent en gradient brand → accent
 *  - Sub-headline
 *  - 2 CTAs (primaire emerald + secondaire outline)
 *  - Bullets de réassurance (CheckCircle2 brand)
 *  - Micro-promise mono (sous bullets)
 *  - Side slot configurable (ParticulierLiveCard / PromiseList / illustration)
 *
 * Tous les éléments sauf le hero/titre/CTA sont optionnels pour permettre
 * de calibrer le hero selon le type de page (hub, guide, stub).
 */

import { motion } from 'framer-motion';
import { ArrowRight, ArrowUpRight, CheckCircle2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { Container } from '../../layout/Container';
import { Button } from '../../ui/button';
import { Reveal } from '../../motion/Reveal';
import { getIcon } from '../../../lib/lucide-icons';
import { cn } from '../../../lib/cn';
import { easeStripe } from '../../../lib/motion';
import {
  PARTICULIER_GRADIENT,
  PARTICULIER_SECTION,
  type ParticulierCta,
} from '../../../config/audience/particulier.ui';
import { ParticulierBackground } from './Background';
import { ParticulierBreadcrumb } from './Breadcrumb';
import { ParticulierEyebrow } from './Eyebrow';

export interface ParticulierPageHeroProps {
  /** Items du fil d'ariane (sans préfixe "Accueil / Particuliers") */
  breadcrumb?: Array<{ name: string; href?: string }>;
  /**
   * Préfixe custom du fil d'Ariane (override le défaut "Accueil / Particuliers").
   * Utilisé par les pages d'autres audiences (Agriculteurs notamment).
   */
  breadcrumbPrefix?: Array<{ name: string; href?: string }>;
  /** Texte de l'eyebrow (chip Linear). Si null/undefined, l'eyebrow n'est pas affichée. */
  eyebrow?: string;
  /** Icône Lucide affichée dans l'eyebrow (si défini) */
  eyebrowIconName?: string;
  /** Titre H1 — partie en couleur ink */
  headline: string;
  /** Titre H1 — partie en gradient brand → accent */
  headlineAccent?: string;
  /** Description sous le titre */
  subheadline?: ReactNode;
  /** CTA primaire (variant=primary emerald) */
  primaryCta: ParticulierCta;
  /** CTA secondaire (variant=secondary outline) */
  secondaryCta?: ParticulierCta;
  /** Bullets de réassurance (CheckCircle2 brand) */
  bullets?: string[];
  /** Micro-promise mono (sous bullets) */
  microPromise?: string;
  /** Mini chips d'actions (tag-mono Linear) sous le hero */
  miniActions?: Array<{ label: string; href: string; iconName: string }>;
  /** Slot droit — typiquement une ParticulierLiveCard ou une ParticulierPromiseList */
  sideSlot?: ReactNode;
  /** Padding du hero (par défaut : standard) */
  paddingClass?: string;
  /** Affiche un badge text à droite de l'eyebrow (ex. "120 fiches publiées") */
  eyebrowTrailingNote?: string;
}

export function ParticulierPageHero({
  breadcrumb,
  breadcrumbPrefix,
  eyebrow,
  eyebrowIconName,
  headline,
  headlineAccent,
  subheadline,
  primaryCta,
  secondaryCta,
  bullets,
  microPromise,
  miniActions,
  sideSlot,
  paddingClass,
  eyebrowTrailingNote,
}: ParticulierPageHeroProps) {
  const PrimaryIcon = primaryCta.iconName ? getIcon(primaryCta.iconName) : null;
  const SecondaryIcon = secondaryCta?.iconName ? getIcon(secondaryCta.iconName) : null;

  return (
    <section
      className={cn(
        'relative overflow-hidden bg-paper',
        paddingClass ?? PARTICULIER_SECTION.hero,
      )}
    >
      <ParticulierBackground />

      <Container className="relative">
        {breadcrumb && breadcrumb.length > 0 && (
          <ParticulierBreadcrumb trail={breadcrumb} prefix={breadcrumbPrefix} />
        )}

        <div
          className={cn(
            'grid items-start gap-10 lg:gap-14',
            sideSlot ? 'lg:grid-cols-[1.1fr_1fr]' : 'lg:grid-cols-1',
          )}
        >
          {/* ── Colonne TEXTE ── */}
          <div>
            {eyebrow && (
              <Reveal>
                <div className="flex flex-wrap items-center gap-2">
                  <ParticulierEyebrow withDot iconName={eyebrowIconName}>
                    {eyebrow}
                  </ParticulierEyebrow>
                  {eyebrowTrailingNote && (
                    <span className="hidden text-[11.5px] text-stone-500 md:inline">
                      {eyebrowTrailingNote}
                    </span>
                  )}
                </div>
              </Reveal>
            )}

            <Reveal delay={0.06}>
              <h1 className="mt-6 max-w-2xl text-balance font-display text-[clamp(2rem,4.5vw,3.25rem)] font-bold leading-[1.04] tracking-[-0.03em] text-ink">
                {headline}
                {headlineAccent && (
                  <>
                    {' '}
                    <span className={PARTICULIER_GRADIENT.headline}>{headlineAccent}</span>
                  </>
                )}
              </h1>
            </Reveal>

            {subheadline && (
              <Reveal delay={0.14}>
                <div className="mt-5 max-w-xl text-[15px] leading-relaxed text-stone-600">
                  {subheadline}
                </div>
              </Reveal>
            )}

            <Reveal delay={0.22}>
              <div className="mt-7 flex flex-wrap items-center gap-2.5">
                <Button
                  asChild
                  size="lg"
                  variant="primary"
                  className="group shadow-cta-brand hover:shadow-cta-brand-hover"
                >
                  <a href={primaryCta.href}>
                    {PrimaryIcon && <PrimaryIcon className="h-3.5 w-3.5" strokeWidth={2} />}
                    {primaryCta.label}
                    <ArrowRight className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover/btn:translate-x-0.5" />
                  </a>
                </Button>
                {secondaryCta && (
                  <Button asChild size="lg" variant="secondary" className="group">
                    <a href={secondaryCta.href}>
                      {SecondaryIcon && <SecondaryIcon className="h-3.5 w-3.5" strokeWidth={2} />}
                      {secondaryCta.label}
                      <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                    </a>
                  </Button>
                )}
              </div>
            </Reveal>

            {bullets && bullets.length > 0 && (
              <Reveal delay={0.32}>
                <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px] text-stone-600">
                  {bullets.map((b) => (
                    <span key={b} className="inline-flex items-center gap-1.5">
                      <CheckCircle2 className="h-3 w-3 text-brand-500" strokeWidth={2.2} />
                      {b}
                    </span>
                  ))}
                </div>
              </Reveal>
            )}

            {microPromise && (
              <Reveal delay={0.42}>
                <p className="mt-3 inline-flex items-center gap-1.5 font-mono text-[11px] text-stone-500">
                  <span className="inline-block h-1 w-1 rounded-full bg-stone-400" />
                  {microPromise}
                </p>
              </Reveal>
            )}

            {miniActions && miniActions.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.5, delay: 0.5, ease: easeStripe }}
                className="mt-7 flex flex-wrap items-center gap-1.5"
              >
                {miniActions.map((action) => {
                  const Icon = getIcon(action.iconName);
                  return (
                    <a
                      key={action.label}
                      href={action.href}
                      className="tag-mono group hover:-translate-y-0.5"
                    >
                      <Icon className="h-3 w-3 shrink-0" strokeWidth={2} />
                      <span>{action.label}</span>
                      <ArrowUpRight
                        className="h-3 w-3 shrink-0 opacity-50 transition-all duration-150 ease-out group-hover:translate-x-0.5 group-hover:opacity-100"
                        strokeWidth={2}
                      />
                    </a>
                  );
                })}
              </motion.div>
            )}
          </div>

          {/* ── Colonne SIDE SLOT ── */}
          {sideSlot && <div className="relative">{sideSlot}</div>}
        </div>
      </Container>
    </section>
  );
}
